import React from 'react'
import { debounce } from 'utils/function'

export const useAsyncSelect = (fetchHandler, matcher, transformData) => {
  const [offset] = React.useState(0)
  const [isFetching, setIsFetching] = React.useState(true)
  // a fetch can finish after the select has gone, for example when the page
  // changes, and must not set state then
  const isMounted = React.useRef(true)
  React.useEffect(
    () => () => {
      isMounted.current = false
    },
    [],
  )

  const loadOptions = React.useCallback(
    async (search, callback) => {
      setIsFetching(true)

      try {
        const { response = {} } = await fetchHandler({
          limit: 50,
          offset,
          search,
        })

        const results = response?.results ?? []

        callback(
          (transformData ? transformData(results) : results).map(item =>
            matcher(item),
          ),
        )
      } catch (error) {
        console.error(error)
      } finally {
        if (isMounted.current) setIsFetching(false)
      }
    },
    [matcher, offset, fetchHandler],
  )

  // `loadOptions` is rebuilt whenever its dependencies change, and several
  // callers pass a matcher defined inline in their render, so it changes on
  // every render. Debouncing it directly - as this hook used to - produced a
  // fresh debounced function each time and discarded the pending timer with
  // it, so the search was never actually debounced. Holding the latest
  // callback in a ref keeps the debounced function itself stable for the life
  // of the component, which is what makes the delay work.
  const latestLoadOptions = React.useRef(loadOptions)
  React.useEffect(() => {
    latestLoadOptions.current = loadOptions
  }, [loadOptions])

  const debouncedLoadOptions = React.useMemo(
    () =>
      debounce(
        (search: string, callback: (options: unknown[]) => void) =>
          latestLoadOptions.current(search, callback),
        500,
      ),
    [],
  )

  React.useEffect(() => debouncedLoadOptions.cancel, [debouncedLoadOptions])

  return {
    state: { isFetching },
    handlers: {
      loadOptions: debouncedLoadOptions,
    },
  }
}
